<script lang="ts" setup>
import SFButton from '@/components/ui/button/Button.vue'
import Dropdown from '@/components/ui/dropdown/Dropdown.vue'
import { inject, ref } from 'vue'
import { FireRiskDashboardViewModel } from '../viewmodels/FireRiskDashboard.viewmodel'
import DataPicker from '@/components/ui/dataPicker/DataPicker.vue'
import { FireRiskEnum } from '@/@core/fire-risk-dashboard/domain/enum/FireRisk.enum'
import { FireOutbreakOccurrenceEnum } from '@/@core/fire-risk-dashboard/domain/enum/FireOutbreakOccurence.enum'
import { FireOutbreakReactionEnum } from '@/@core/fire-risk-dashboard/domain/enum/FireOutbreakReaction.enum'
import { FireAlertsFilterDto } from '../dtos/FireAlertsFilter.dto'
import { onClickOutside } from '@vueuse/core'
import { FireRiskDashboardLabelProvider } from '../providers/FireRiskDashboardLabel.provider'

const filter = ref()

const vm = inject<FireRiskDashboardViewModel>('fire-risk-dashboard-viewmodel')!

const fireAlertsFilter = ref<FireAlertsFilterDto>({ ...vm.initFireAlertsFilter })

const isFileringOpen = ref(false)

// FireRiskEnum.NONE ('NAO_ESPECIFICADO', exibido como "Nulo") fica fora do filtro por
// decisao de produto; o enum continua com o valor porque a API ainda pode envia-lo.
const riskTypes = [
	FireRiskEnum.NULL,
	FireRiskEnum.SMALL,
	FireRiskEnum.MEDIUM,
	FireRiskEnum.HIGH,
	FireRiskEnum.HIGHEST,
].map((risk) => ({ label: FireRiskDashboardLabelProvider.risk(risk), value: risk }))

const occurrence = [FireOutbreakOccurrenceEnum.FARM, FireOutbreakOccurrenceEnum.BUFFER].map(
	(occurrence) => ({
		label: FireRiskDashboardLabelProvider.occurrence(occurrence),
		value: occurrence,
	}),
)

const reactions = [
	FireOutbreakReactionEnum.POSITIVE,
	FireOutbreakReactionEnum.NEGATIVE,
	FireOutbreakReactionEnum.IMPOSSIBLE,
	undefined,
].map((reaction) => ({ label: FireRiskDashboardLabelProvider.reaction(reaction), value: reaction }))

function setFilter() {
	vm.fireAlertsFilter.value = { ...fireAlertsFilter.value }
	closeFilter()
}

function clearFilter() {
	fireAlertsFilter.value = { ...vm.initFireAlertsFilter }
	vm.clearFilter()
	closeFilter()
}

function openFilter() {
	// O filtro pode ter sido limpo por fora (empty state da tabela), entao a copia
	// local e re-sincronizada a cada abertura para nao mostrar valores obsoletos.
	fireAlertsFilter.value = { ...vm.fireAlertsFilter.value }
	isFileringOpen.value = true
}

function closeFilter() {
	isFileringOpen.value = false
}

onClickOutside(filter, closeFilter)
</script>

<template>
	<div class="relative">
		<s-f-button
			class="h-full"
			icon="flowbite:filter-solid"
			secondary
			@click="openFilter"
		/>
		<div
			ref="filter"
			v-show="isFileringOpen"
			class="bg-white-neutral border border-black-neutral rounded min-w-80 absolute z-10 p-3 flex flex-col gap-4 -translate-x-[102%] -translate-y-1/2"
		>
			<dropdown
				:options="vm.farms.value"
				v-model="fireAlertsFilter.farms"
				:placeholder="$t('fireRiskDashboardSelect').toString()"
				:label="$t('fireRiskDashboardFarmSector').toString()"
			/>
			<div class="flex flex-col gap-1">
				<label class="text-label">{{ $t('fireRiskDashboardDate') }}</label>
				<data-picker
					:max-date="new Date().toISOString()"
					v-model="fireAlertsFilter.startDate"
				/>
				<data-picker
					:min-date="fireAlertsFilter.startDate"
					:max-date="new Date().toISOString()"
					v-model="fireAlertsFilter.endData"
				/>
			</div>
			<div class="flex flex-col">
				<label class="text-label text-center">{{
					$t('fireRiskDashboardDetectionConfidencePercent')
				}}</label>
				<div class="flex items-center gap-1 w-full">
					<span class="text-s-bold !text-blue-tints">{{
						fireAlertsFilter.detectionConfidence.at(0)
					}}</span>
					<v-range-slider
						v-model="fireAlertsFilter.detectionConfidence"
						color="#34617E"
						track-color="#34617E"
						max="100"
						min="0"
						hide-details
					/>
					<span class="text-s-bold !text-blue-tints">{{
						fireAlertsFilter.detectionConfidence.at(1)
					}}</span>
				</div>
			</div>
			<dropdown
				:options="riskTypes"
				v-model="fireAlertsFilter.risks"
				:placeholder="$t('fireRiskDashboardSelect').toString()"
				:label="$t('fireRiskDashboardRiskType').toString()"
			/>
			<dropdown
				:options="occurrence"
				v-model="fireAlertsFilter.occurrences"
				:label="$t('fireRiskDashboardOccurrenceLocation').toString()"
			/>
			<dropdown
				:options="reactions"
				v-model="fireAlertsFilter.reactions"
				:label="$t('fireRiskDashboardAlertEvaluation').toString()"
			/>
			<div class="flex justify-between gap-4">
				<s-f-button
					:text="$t('fireRiskDashboardClearFilters').toString()"
					secondary
					class="w-1/2 justify-center"
					@click="clearFilter"
				/>
				<s-f-button
					:text="$t('fireRiskDashboardFilter').toString()"
					class="w-1/2 justify-center"
					@click="setFilter"
				/>
			</div>
		</div>
	</div>
</template>
